"use client";

import React from "react";
import { Facebook, Instagram, Linkedin, MapPin, Mail, Phone, CircleCheck, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-3">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-24 bg-muted/10 animate-pulse rounded" data-index={i} />)}
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    return <nav className="flex flex-col gap-3">
        {["Home", "About Us", "Services", "Contact"].map((label, i) => <Link key={i} to="#" className="text-muted-foreground hover:text-primary transition-colors text-sm" data-index={i}>
            {label}
          </Link>)}
      </nav>;
  }
  return <nav className="flex flex-col gap-3">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-muted-foreground hover:text-primary transition-colors text-sm flex items-center gap-2 group" data-index={i}>
          <span className="h-px w-0 bg-primary transition-all duration-300 group-hover:w-2" />
          {item.label}
        </Link>)}
    </nav>;
}
function FooterMenu() {
  return <MenuProvider menu_id="48" className="w-full">
      <FooterMenuConsumer />
    </MenuProvider>;
}
export default function SiteFooter() {
  const currentYear = new Date().getFullYear();
  const businessName = "Gold Star Executive Cleaning";
  const missionStatement = "Setting the gold standard in commercial cleaning. We provide reliable, high-quality cleaning solutions for businesses that demand excellence.";
  const serviceAreas = ["Newcastle", "Central Coast", "Sydney"];
  const phone = "0412 922 689";
  const email = "info@goldstarexecutivecleaning.com.au";
  const associations = [];
  const socialLinks = [{
    icon: Facebook,
    href: "https://www.facebook.com/goldstarexecutivecleaning",
    label: "Facebook"
  }, {
    icon: Instagram,
    href: "https://www.instagram.com/goldstarexecutivecleaning2022/",
    label: "Instagram"
  }];
  return <footer id="site-footer" className="dark bg-background text-foreground border-t border-border/10">
      {}
      <div className="container mx-auto px-4 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          
          {}
          <div className="flex flex-col gap-6">
            <Link to="/" className="block w-fit">
              <img src="https://goldstar-executive.websiteshop.au/wp-content/uploads/2026/03/Logo-White.png" alt={businessName} className="h-10 w-auto object-contain" />
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed max-w-xs">
              {missionStatement}
            </p>
            <div className="flex items-center gap-2 text-sm text-primary font-medium">
              <CircleCheck className="w-4 h-4" />
              <span>Fully Insured & Trusted</span>
            </div>
          </div>

          {}
          <div className="flex flex-col gap-6">
            <h3 className="text-lg font-semibold text-foreground tracking-tight">Quick Links</h3>
            <FooterMenu />
          </div>

          {}
          <div className="flex flex-col gap-6">
            <h3 className="text-lg font-semibold text-foreground tracking-tight">Service Areas</h3>
            <ul className="flex flex-col gap-4">
              {serviceAreas.map((area, i) => <li key={area} className="flex items-start gap-3 text-sm text-muted-foreground" data-index={i}>
                  <MapPin className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <span className="hover:text-foreground transition-colors cursor-default">
                    {area} Region
                  </span>
                </li>)}
              <li className="mt-2">
                
              </li>
            </ul>
          </div>

          {}
          <div className="flex flex-col gap-6">
            <h3 className="text-lg font-semibold text-foreground tracking-tight">Connect</h3>
            <div className="flex flex-col gap-4">
              <Link to={`tel:${phone.replace(/\D/g, '')}`} className="flex items-center gap-3 text-sm text-muted-foreground hover:text-primary transition-colors group">
                <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                  <Phone className="w-4 h-4 text-primary" />
                </div>
                {phone}
              </Link>
              <Link to={`mailto:${email}`} className="flex items-center gap-3 text-sm text-muted-foreground hover:text-primary transition-colors group">
                <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                  <Mail className="w-4 h-4 text-primary" />
                </div>
                {email}
              </Link>
            </div>
            
            <div className="mt-4">
              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">Follow Us</h4>
              <div className="flex gap-3">
                {socialLinks.map((social, i) => <Link key={i} to={social.href} newTab className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300 shadow-sm hover:shadow-md hover:-translate-y-1" aria-label={social.label} data-index={i}>
                    <social.icon className="w-5 h-5" />
                  </Link>)}
              </div>
            </div>
          </div>

        </div>
      </div>

      <Separator className="bg-border/10" />

      {}
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
          
          {}
          <div className="text-xs text-muted-foreground text-center md:text-left">
            <p>&copy; {currentYear} {businessName}. All rights reserved.</p>
            <div className="mt-2 flex gap-4 justify-center md:justify-start">
              <Link to="/privacy-policy" className="hover:text-primary transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-primary transition-colors">Terms of Service</Link>
            </div>
          </div>

          {}
          <div className="flex flex-wrap justify-center items-center gap-6 grayscale opacity-50 hover:grayscale-0 hover:opacity-100 transition-all duration-500">
            {associations.map((assoc, i) => <img key={i} src={assoc.img} alt={assoc.name} title={assoc.name} className="h-8 w-auto object-contain" data-index={i} />)}
          </div>

        </div>
      </div>
    </footer>;
}